import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { getEnvironment } from '@minecraft.environment';
import {
  InitiateMediaUploadV1Doc,
  FinalizeMediaUploadV1Doc,
  MediaFileTypeExtension,
  BaseMediaFragment,
  MediaTypeCodeEnum,
  CreateArtistMediaDoc,
  CompleteArtistMediaDoc,
} from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import {
  getEscapedFilename,
  getFileExtension,
  getWidgetUploadPreset,
  buildWidgetValidationRules,
  WIDGET_EVENT_CHECKS,
  checkIf3GPEncoded,
} from './helpers';
import { buildCloudinaryLocalization } from './translations';
import { ControlledWidgetOptions, UploadWidgetOptions, WidgetFileUploaderInput } from './types';

export interface UseUploadWidgetParams {
  /**
   * if artistId is passed, the widget will use the createArtistMedia / completeArtistMedia mutations
   * which will attach the media to the artist's media library or profile
   *
   * if artistId is not passed, the widget will use the initMediaUpload / finalizeMediaUpload mutations
   * which will upload the media for use in self-tapes, but will not attach it to the artist's media library
   * or proifle
   * */
  artistId?: number;
  maxFiles?: number;
  mediaType?: MediaTypeCodeEnum;
  onFinalizeStart?: (succeeded: cloudinary.CFile[], failed: cloudinary.CFile[]) => void;
  onFinalizeUploadError?: (params: { errMsg: string; failedMedia: cloudinary.CFile[] }) => void;
  onInitializeUploadError?: (message: string) => void;
  onSuccessfulUpload?: (params: {
    finalizedMedia: BaseMediaFragment[];
    failedMedia: cloudinary.CFile[];
  }) => Promise<void>;
  uploadWidgetOptions?: Omit<UploadWidgetOptions, ControlledWidgetOptions>;
  /**
   * number of milliseconds to wait on the upload before timing out
   * defaults to 10 minutes (600000ms)
   */
  uploadTimeout?: number;
}

const mapDurationToMilliseconds = (duration?: string | number): number | null => {
  const numDur = Number(duration);
  // Convert duration to millis and round to nearest integer
  return numDur && !Number.isNaN(numDur) ? parseInt(Math.round(numDur * 1000).toFixed(0), 10) : null;
};

export const useUploadWidget = ({
  artistId,
  maxFiles,
  mediaType,
  onFinalizeStart,
  onFinalizeUploadError,
  onInitializeUploadError,
  onSuccessfulUpload,
  uploadWidgetOptions,
  uploadTimeout = 600000,
}: UseUploadWidgetParams) => {
  const environment = getEnvironment();
  const cloudinaryHotStorage = environment?.CN_HOT_CLOUDNAME;
  const widgetRef = useRef<cloudinary.WidgetInterface>(null);
  const { t, i18n } = useTranslation();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [initMediaUploadMutation] = useMutation(InitiateMediaUploadV1Doc);
  const [finalizeMediaUploadMutation] = useMutation(FinalizeMediaUploadV1Doc);

  const [createArtistMediaMutation] = useMutation(CreateArtistMediaDoc);
  const [completeArtistMediaMutation] = useMutation(CompleteArtistMediaDoc);

  const widgetTextAssets = buildCloudinaryLocalization(i18n.language, t);
  const loadScript = useCallback(() => {
    if (!isScriptLoaded) {
      const uwScript = document.getElementById('uw');

      if (!uwScript) {
        const script = document.createElement('script');
        script.setAttribute('async', '');
        script.setAttribute('id', 'uw');
        script.src = 'https://upload-widget.cloudinary.com/global/all.js';
        script.addEventListener('load', () => setIsScriptLoaded(true));
        document.body.appendChild(script);
      } else {
        setIsScriptLoaded(true);
      }
    }
  }, [isScriptLoaded]);

  // lazy load the script
  useEffect(() => {
    loadScript();
  });

  const finalizeMediaUploadHelper = useCallback(
    async (media: cloudinary.CFile[] = []) => {
      // sort out which files succeeded and which failed
      const { failed, succeeded } = media.reduce(
        (acc, m) => {
          if (m.failed) {
            acc.failed.push(m);
          } else {
            acc.succeeded.push(m);
          }

          return acc;
        },
        { failed: [] as cloudinary.CFile[], succeeded: [] as cloudinary.CFile[] }
      );

      // close the widget, and let the uploader component know
      // to render the finalizing loading state
      widgetRef?.current?.close();
      onFinalizeStart?.(succeeded, failed);

      try {
        // if all files failed, throw an error
        if (succeeded.length === 0) {
          throw new Error('No files were uploaded.');
        }

        // finalize any media that have succeeded
        const MediaMetadataInput = succeeded.map((info) => ({
          // publicId is the guid for this file returned by the init media call
          guid: info.preparedParams.publicId,
          mediaId: info.preparedParams.mediaId,
          filename: info.uploadInfo.original_filename,
          size: info.uploadInfo.bytes,
          width: info.uploadInfo?.width,
          height: info.uploadInfo?.height,
          format: info.uploadInfo?.format,
          length: typeof info?.uploadInfo?.duration === 'number' ? Math.floor(info.uploadInfo?.duration) : null,
        }));

        let finalizedMedia: BaseMediaFragment[] = [];

        if (artistId) {
          const response = await completeArtistMediaMutation({
            variables: {
              input: {
                artistId,
                media: MediaMetadataInput.map((m) => ({
                  duration: m?.length?.toString(),
                  height: m?.height?.toString(),
                  mediaId: m?.mediaId,
                  size: m?.size,
                  width: m?.width?.toString(),
                })),
              },
            },
          });

          if (response?.errors || !response?.data?.completeArtistMediaCreation) {
            throw response?.errors[0] || new Error('finalize media upload failed');
          }

          finalizedMedia = response?.data?.completeArtistMediaCreation?.media;
        } else {
          const response = await finalizeMediaUploadMutation({
            variables: {
              MediaMetadataInput: MediaMetadataInput.map(({ mediaId: _, ...mediaInput }) => mediaInput),
            },
          });

          if (response?.errors || !response?.data?.finalizeMediaUpload) {
            throw response?.errors[0] || new Error('finalize media upload failed');
          }

          finalizedMedia = response?.data?.finalizeMediaUpload;
        }

        await onSuccessfulUpload?.({ finalizedMedia, failedMedia: failed });
        return { finalizedMedia, failedMedia: failed };
      } catch (error) {
        const specificErrMsg =
          error?.graphQLErrors?.[0]?.data?.errorData?.message ||
          error?.graphQLErrors?.[0]?.extensions?.exception?.publicData?.message;
        await widgetRef?.current?.destroy();
        onFinalizeUploadError?.({ errMsg: specificErrMsg || (error as Error).message, failedMedia: failed });
        return { finalizedMedia: [], failedMedia: failed };
      }
    },
    [
      artistId,
      completeArtistMediaMutation,
      finalizeMediaUploadMutation,
      onFinalizeStart,
      onFinalizeUploadError,
      onSuccessfulUpload,
      widgetRef,
    ]
  );

  const initializeCloudinaryWidgetSilently = (filesToUpload: WidgetFileUploaderInput[]) => {
    let finalizedMedia = [];
    let failedMedia = [];
    let complete = false;

    if (isScriptLoaded) {
      let cloudinaryUploadOrder: cloudinary.PreBatchDataFile[] = [];

      if (widgetRef.current) {
        widgetRef.current.destroy().catch((e) => console.error(e));
      }

      widgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: cloudinaryHotStorage,
          uploadPreset: getWidgetUploadPreset(mediaType) || 'ml_default',
          multiple: maxFiles !== 1,
          sources: ['local'],
          showUploadMoreButton: false,
          showPoweredBy: false,
          maxFiles,
          ...buildWidgetValidationRules(mediaType),
          ...uploadWidgetOptions,
          language: i18n.language,
          text: widgetTextAssets,
          preBatch: (cb, data) => {
            cloudinaryUploadOrder = data.files;
            cb();
          },
          prepareUploadParams: async (cb, params: cloudinary.UploadParam | cloudinary.UploadParam[]) => {
            const uploadingFilesMap = new Map(
              filesToUpload.map((f) => {
                return [f.originalFile.name, f];
              })
            );

            const MediaMetadataInput = cloudinaryUploadOrder.map(({ name }) => {
              const { originalFile: file, width, height, duration } = uploadingFilesMap.get(name);

              return {
                fullName: file.name,
                fileName: getEscapedFilename(file),
                fileSize: file.size,
                fileWidth: width || -1,
                fileHeight: height || -1,
                fileType: getFileExtension(file.name) as MediaFileTypeExtension,
                fileDuration: mapDurationToMilliseconds(duration),
              };
            });

            try {
              let initResMedia: { mediaId: number; guid: string }[] = [];
              if (artistId) {
                const response = await createArtistMediaMutation({
                  variables: {
                    input: {
                      artistId,
                      media: MediaMetadataInput.map((m) => ({ filename: m.fullName })),
                    },
                  },
                });

                if (
                  response?.errors ||
                  !response?.data?.createArtistMedia ||
                  response?.data?.createArtistMedia?.media?.length !== MediaMetadataInput.length
                ) {
                  throw response?.errors[0] || new Error('init media upload failed');
                }

                initResMedia = response.data.createArtistMedia.media;
              } else {
                const response = await initMediaUploadMutation({
                  variables: { MediaInitUploadInput: { count: MediaMetadataInput.length } },
                });

                if (response?.errors || !response?.data?.initMediaUpload) {
                  throw response?.errors[0] || new Error('init media upload failed');
                }

                initResMedia = response.data.initMediaUpload;
              }

              const newParams = cloudinaryUploadOrder.map(async ({ name }, index) => {
                const { originalFile: file } = uploadingFilesMap.get(name);
                const fileNameKey = getEscapedFilename(file).toUpperCase();
                const fileExt = getFileExtension(file.name).toUpperCase();
                const initializedMedia = initResMedia[index];
                const is3GPEncoded = mediaType === MediaTypeCodeEnum.Video && (await checkIf3GPEncoded(file));
                const uploadParams = { ...params } as cloudinary.UploadParam & { upload_preset: string };
                if (is3GPEncoded) {
                  const uploadPresetNoTransform = `${uploadParams.upload_preset}-no-transform`;
                  uploadParams.uploadPreset = uploadPresetNoTransform;
                  // eslint-disable-next-line camelcase
                  uploadParams.upload_preset = uploadPresetNoTransform;
                }

                return {
                  ...uploadParams,
                  mediaId: initializedMedia.mediaId,
                  publicId: initializedMedia.guid,
                  fileType: fileExt,
                  filename: fileNameKey,
                };
              });

              const resolvedParams = await Promise.all(newParams);
              cb(resolvedParams);
            } catch (error) {
              const specificErrMsg =
                error?.graphQLErrors?.[0]?.data?.errorData?.message ||
                error?.extensions?.exception?.publicData?.message;
              await widgetRef?.current?.destroy();
              onInitializeUploadError?.(specificErrMsg || (error as Error).message);
            }
          },
        },
        async (_, eventResult) => {
          if (WIDGET_EVENT_CHECKS['queues-end'](eventResult)) {
            // once all the files have processed, finalize the upload
            // it's possible all the failed succeeded,
            // all the files failed, or a mix of both
            const uploadResults = await finalizeMediaUploadHelper(eventResult.info.files);
            finalizedMedia = uploadResults.finalizedMedia;
            failedMedia = uploadResults.failedMedia;
            complete = true;
          }
        }
      );

      widgetRef.current.open(null, { files: filesToUpload.map((f) => f.originalFile) });
    }

    return {
      completedUpload: () =>
        new Promise<{
          finalizedMedia: BaseMediaFragment[];
          failedMedia: cloudinary.CFile[];
        }>((resolve, reject) => {
          const startTime = Date.now();
          const interval = setInterval(() => {
            if (complete) {
              clearInterval(interval);
              resolve({ finalizedMedia, failedMedia });
            }

            if (Date.now() - startTime > uploadTimeout) {
              clearInterval(interval);
              reject(new Error('Upload timed out'));
            }
          }, 500);
        }),
    };
  };

  return {
    initializeCloudinaryWidgetSilently,
  };
};
